/**
 * The Terms & Conditions editor.
 *
 * The six bullets were hardcoded in the print template, which was right for the standard
 * quote and wrong for every other one: a rep whose customer negotiated different payment
 * terms could either send a quote stating something untrue, or rebuild it by hand outside
 * the tool. Both happen, and only one of them is visible to us.
 *
 * Per-quote only. The defaults are the standard wording and stay that way — editing here
 * cannot change what anybody else's quote says, which is what makes it safe to let a rep
 * edit legal text at all.
 */
import { Plus, RotateCcw, Trash2 } from 'lucide-react';
import { Button, Input, Label } from '@staff-app/ui';
import { DEFAULT_TERM_TEXTS, type QuoteTerm } from '../terms';

export interface QuoteTermsEditorProps {
  terms: QuoteTerm[];
  onChange: (id: string, text: string) => void;
  onAdd: () => void;
  onRemove: (id: string) => void;
  onRestoreDefaults: () => void;
}

/** True when the list still matches the standard six, in order and verbatim. */
function isDefault(terms: QuoteTerm[]): boolean {
  const texts = terms.map((t) => t.text);
  return (
    texts.length === DEFAULT_TERM_TEXTS.length &&
    texts.every((t, i) => t === DEFAULT_TERM_TEXTS[i])
  );
}

export function QuoteTermsEditor({
  terms,
  onChange,
  onAdd,
  onRemove,
  onRestoreDefaults,
}: QuoteTermsEditorProps) {
  const modified = !isDefault(terms);

  return (
    <section
      className="flex flex-col gap-3 rounded-lg border bg-card p-4 shadow-sm"
      data-testid="quote-terms-card"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          <h2 className="text-sm font-semibold text-foreground">{'Quote Terms & Conditions'}</h2>
          <p className="text-[11px] text-muted-foreground">
            {'Edits apply to this quote only and won’t persist.'}
          </p>
        </div>
        {/* Only offered once something has changed — a reset button on an untouched list is
            noise, and worse, implies the list might have drifted when it has not. */}
        {modified ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={onRestoreDefaults}
            className="cursor-pointer text-xs"
            data-testid="quote-terms-restore"
          >
            <RotateCcw className="mr-1 h-3 w-3" />
            {'Restore the standard terms'}
          </Button>
        ) : null}
      </div>

      {terms.length === 0 ? (
        <p className="text-xs text-warning" data-testid="quote-terms-empty">
          {'No terms — the quote will print without a Terms & Conditions section.'}
        </p>
      ) : (
        <ul className="flex flex-col gap-2" data-testid="quote-terms-list">
          {terms.map((t, i) => (
            <li key={t.id} className="flex items-center gap-2" data-testid={`quote-term-${t.id}`}>
              <span
                aria-hidden
                className="w-4 shrink-0 text-center text-xs text-muted-foreground"
              >
                {'•'}
              </span>
              <Input
                value={t.text}
                onChange={(e) => onChange(t.id, e.target.value)}
                placeholder="Term or condition"
                aria-label={`Term ${i + 1}`}
                data-testid={`quote-term-${t.id}-text`}
              />
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => onRemove(t.id)}
                aria-label={`Remove term ${i + 1}`}
                className="cursor-pointer text-muted-foreground hover:text-destructive"
                data-testid={`quote-term-${t.id}-remove`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={onAdd}
          className="cursor-pointer"
          data-testid="quote-terms-add"
        >
          <Plus className="mr-1 h-4 w-4" />
          {'Add Bullet Point'}
        </Button>
        <Label className="text-[11px] font-normal text-muted-foreground">
          {'A blank bullet is dropped rather than printed empty.'}
        </Label>
      </div>
    </section>
  );
}
